import { StorageKeys } from 'common/storage/constants';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Redirect } from 'react-router';
import { useShowLoginOrSignUpModal } from 'src/auth/modal/useShowLoginOrSignUpModal';
import { useReloadCartFoodItems } from 'src/cart/hooks/useReloadCartFoodItems';
import { RateRecentGroupOrderModalContainer } from 'src/pages/components/ratings/RateRecentGroupOrderModal/RateRecentGroupOrderModalContainer';
import { CuisineCategoryPageHero } from 'src/pages/consumer/dish-first/explore/components/PageHero/CuisineCategoryPageHero/CuisineCategoryPageHero';
import { PageHero } from 'src/pages/consumer/dish-first/explore/components/PageHero/PageHero';
import { VerifiedDietaryPageHero } from 'src/pages/consumer/dish-first/explore/components/PageHero/VerifiedDietaryPageHero/VerifiedDietaryPageHero';
import { PageSegments } from 'src/pages/consumer/dish-first/explore/components/Segments/PageSegments';
import { PageHeroType } from 'src/pages/consumer/dish-first/explore/Constants';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useGetSignupPromo } from 'src/shared/hooks/useGetSignupPromo';
import { Routes } from 'src/shared/Routes';
import { useLocalStorage } from 'src/shared/storage/Storage';
import { useDishFistCartPositionContext } from '../../components/ConsumerHeaderWrapper/CartPositionContext';
import { useShowConsumerHeaderPersistentBanner } from '../../components/header-v2/useShowConsumerHeaderPersistentBanner';
import { useMealPlanSurfacingLogic } from '../../meal-plans/hooks/useMealPlanSurfacingLogic';
import { BulkDiscountModal } from './components/BulkDiscountModal';
import { ExploreFilters } from './components/ExploreFilters';
import { Loader } from './components/Loader';
import { ExploreMealPlanBanner } from './components/MealPlan/ExploreMealPlanBanner';
import ExploreMealPlansModal from './components/MealPlan/ExploreMealPlansModal';
import { BrowseFilterHero } from './components/PageHero/BrowseFilterHero';
import { getSegments } from './components/Segments/PageSegments/PageSegments';
import { ViewCartButton } from './components/ViewCartButton';
import { MealTypeMenuBarProvider } from './context';
import { useCuisineSegmentMarketingUrlPatch, useDishFirstExploreContents, usePageHeroType } from './hooks';
import { ContentsContainer, ExploreCol } from './styles';

export const DishFirstExploreContents = () => {
  useReloadCartFoodItems();
  useCuisineSegmentMarketingUrlPatch();

  const cartPositionState = useDishFistCartPositionContext();
  const { enabled: showPromoBanner } = useShowConsumerHeaderPersistentBanner();
  const [isShowingMealPlansModal, setIsShowingMealPlansModal] = useState(false);
  const [currentUser, currentUserIsLoading] = useCurrentUser();

  const {
    activeZipCode,
    additionalContent,
    allCuisineCategories,
    activeCuisineCategory,
    availableDeliveryDays,
    availableCuisineCategories,
    contentLoading,
    data,
    filters,
    foodItemTotal,
    foodPreferenceSelected,
    handleDateChange,
    infiniteScrollProps,
    isCuisineSegment,
    isFiltersLoading,
    isOnSeeAllPage,
    isPageLoading,
    isZipCodeLoading,
    segment,
    setTopOfPageRef,
    showLoader,
    timeZone,
    totalShefs,
    updateQueryParams,
    zipCodeQueryData,
  } = useDishFirstExploreContents();
  const { show: showLoginModal } = useShowLoginOrSignUpModal();
  const { signupPromo, loading: newUserPromoCodeLoading } = useGetSignupPromo();
  const [hasLoginModalBeenShown] = useLocalStorage(StorageKeys.LOGIN_ON_EXPLORE_MODAL, false);
  const { region } = zipCodeQueryData?.zipCode || {};
  const { pageHeroType, isPageHeroFilterBanner, isPageHeroMain, showBrowseFilterBanner } = usePageHeroType({
    region,
    activeCuisineCategory,
    isPageLoading,
    isCuisineSegment,
    filters,
  });

  const [visitedExplorePage, setVisitedExplorePage] = useLocalStorage<boolean>(StorageKeys.VISITED_EXPLORE_PAGE, false);

  const { shouldShowMealPlanModal, setHasSeenMealPlanModal } = useMealPlanSurfacingLogic();

  useEffect(() => {
    if (newUserPromoCodeLoading) return;
    if (!visitedExplorePage) {
      setVisitedExplorePage(true);
    }

    if (shouldShowMealPlanModal) {
      setIsShowingMealPlansModal(true);
    } else if (!currentUserIsLoading && currentUser == null && !hasLoginModalBeenShown) {
      showLoginModal({ signUpPromoCode: signupPromo });
    }
  }, [currentUserIsLoading, region?.id, newUserPromoCodeLoading]);

  // If they are in a waitlist region, and there are no visible shefs, we want to take them to the waitlist home. The best way
  // to approximate this (without an extra Algolia request), is to see if they have no filters set and there are no results.
  // They could show up with a filter (e.g. vegetarian), and it will say "Sorry, no shefs are cooking under your selected filters."
  // And as soon as they click "clear filters" it will then take them to Waitlist home if there are still no shefs. This is acceptable.
  const {
    dateFilters,
    browseFilter,
    cuisineFilters,
    dietaryFilters,
    dishTypeFilters,
    ecoFilter,
    minRatingFilter,
    priceLevelFilters,
    proteinFilters,
    segmentFilters,
    spiceLevelFilters,
  } = filters;
  const hasFilters = [
    browseFilter,
    dietaryFilters,
    spiceLevelFilters,
    dateFilters,
    cuisineFilters,
    segmentFilters,
    dishTypeFilters,
    ecoFilter,
    minRatingFilter,
    priceLevelFilters,
    proteinFilters,
  ].some((f) => !isEmpty(f));

  const segments = getSegments({ data, filters, updateQueryParams, additionalContent });

  const noShefsNearBy = !isPageLoading && segments.length === 0 && !hasFilters;
  if (!activeZipCode) {
    return <Redirect to={Routes.CONSUMER_LANDING} />;
  }

  if (!isZipCodeLoading && (!zipCodeQueryData?.zipCode?.availableToCustomers || noShefsNearBy)) {
    return <Redirect to={Routes.WAITLIST_HOME} />;
  }

  return (
    <>
      <BulkDiscountModal />
      <InfiniteScroll {...infiniteScrollProps} loader={showLoader && <Loader />} style={{ overflowY: 'hidden' }}>
        <ContentsContainer isCartOpen={cartPositionState.isOpen}>
          {pageHeroType === PageHeroType.CuisineCategoryPage && activeCuisineCategory && (
            <CuisineCategoryPageHero data={data} cuisineCategory={activeCuisineCategory} filters={filters} />
          )}
          {pageHeroType === PageHeroType.VerifiedDietaryPage && (
            <VerifiedDietaryPageHero filters={filters} isOnSeeAllPage={isOnSeeAllPage} segment={segment} />
          )}
          {showBrowseFilterBanner && <BrowseFilterHero cuisines={allCuisineCategories} filters={filters} />}
          {[PageHeroType.Main, PageHeroType.FilterBanner, PageHeroType.Loading].includes(pageHeroType) && (
            <ExploreMealPlanBanner />
          )}
          <ExploreCol promoBannerShowing={showPromoBanner}>
            <MealTypeMenuBarProvider>
              <ExploreFilters
                loading={isFiltersLoading}
                availableDeliveryDays={availableDeliveryDays}
                timeZone={timeZone}
                allCuisineCategories={allCuisineCategories}
                availableCuisineCategories={availableCuisineCategories}
                onDateChange={handleDateChange}
                filters={filters}
                showFiltersInline={!isOnSeeAllPage}
                showCuisineFilterAsDropdown={foodPreferenceSelected}
                totalShefs={totalShefs}
              />

              {isPageLoading && <Loader />}

              <div ref={setTopOfPageRef} />

              {(isPageHeroMain || isPageHeroFilterBanner) && (
                <PageHero filters={filters} segment={segment} total={foodItemTotal} />
              )}

              {!isPageLoading && (
                <PageSegments
                  filters={filters}
                  updateQueryParams={updateQueryParams}
                  data={data}
                  contentLoading={contentLoading}
                  additionalContent={additionalContent}
                />
              )}
            </MealTypeMenuBarProvider>
          </ExploreCol>
        </ContentsContainer>
      </InfiniteScroll>
      <ViewCartButton />
      <ExploreMealPlansModal
        onClose={() => {
          setIsShowingMealPlansModal(false);
          setHasSeenMealPlanModal();
        }}
        isShowing={isShowingMealPlansModal}
      />
      <RateRecentGroupOrderModalContainer />
    </>
  );
};
